import { IconCheck, IconInfoCircle } from '@tabler/icons-react';
import React from 'react';

import styled, { css } from 'styled-components';

import { LinkButton } from 'brand-guidelines/Buttons-styled-components';
import { BRAND_COLORS } from 'brand-guidelines/colors';
import { TYPOGRAPHY } from 'brand-guidelines/typography';
import { applyStylesForScreenSize } from 'brand-guidelines/utils/screenSizeUtils';
import { SCREEN_SIZES } from 'brand-guidelines/utils/screenSizeUtils';
import { Alert } from 'pages/checkout/steps/Insurance/Alert';
import { ALERT_THEMES } from 'pages/checkout/steps/Insurance/Alert-styled-components';
import { Autocomplete } from 'pages/common/components/Autocomplete/Autocomplete';
import { AlignItems, FlexDirection, JustifyContent } from 'types/CSS';
import { svgComponentToMarkupString } from 'utils/styledComponentSvg';

import { ToggleSelect } from './ToggleSelect/ToggleSelect';
import { BaseBodyText } from './typographic-styled-components';

export const CheckoutLinkButton = styled(LinkButton)`
  ${TYPOGRAPHY.CIRCULAR.bold}
  color: ${BRAND_COLORS.darkGreen};
`;

export const CheckoutLink = styled(CheckoutLinkButton).attrs({ as: 'a' })`
  text-align: center;
`;

interface FormLabelProps {
  $direction?: FlexDirection;
  $alignItems?: AlignItems;
  $justifyContent?: JustifyContent;
}

export const FormLabel = styled.label<FormLabelProps>`
  ${TYPOGRAPHY.CIRCULAR.bold}
  color: ${BRAND_COLORS.black};
  font-size: 14px;
  line-height: 21px;

  display: flex;
  gap: 4px;
  ${({ $direction = FlexDirection.COLUMN }) => $direction && css`
    flex-direction: ${$direction};
  `}
  ${({ $alignItems }) => $alignItems && css`
    align-items: ${$alignItems};
  `}
  ${({ $justifyContent }) => $justifyContent && css`
    justify-content: ${$justifyContent};
  `}
`;

export interface FormInputProps {
  $isInvalid?: boolean;
}

export const commonInputStyles = css<FormInputProps>`
  ${TYPOGRAPHY.CIRCULAR.book}
  color: ${BRAND_COLORS.black};
  font-size: 16px;
  line-height: 24px;
  height: 48px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${BRAND_COLORS.stroke};
  background: ${BRAND_COLORS.white};

  transition-property: border-color;
  transition-duration: 100ms;
  transition-timing-function: ease-in-out;

  && {
    &:focus-visible {
      outline: none;
      /* Only show focus border if valid */
      ${({ $isInvalid }) => !$isInvalid && css`
        border-color: ${BRAND_COLORS.lightGreen};
      `}
    }
  }

  ${({ $isInvalid }) => $isInvalid && css`
    border-color: ${BRAND_COLORS.darkRed};
  `}
`;

export const FormInput = styled.input<FormInputProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  ${commonInputStyles}

  &::placeholder {
    color: ${BRAND_COLORS.darkStroke};
  }
`;

export const FormCheckboxWrapper = styled(BaseBodyText).attrs({ as: 'label' })`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  text-align: left;
`;

const IconCheckDataUri = svgComponentToMarkupString(IconCheck, { color: BRAND_COLORS.white, size: 16 });
const IconCheckDataUriMobile = svgComponentToMarkupString(IconCheck, { color: BRAND_COLORS.white, size: 20 });

export const FormCheckbox = styled.input.attrs({ type: 'checkbox' })<FormInputProps>`
  ${commonInputStyles}

  /* Allows us to override the default UA styles */
  appearance: none;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: transparent;

  flex-shrink: 0;

  width: 20px;
  height: 20px;

  border: 1px solid ${BRAND_COLORS.stroke};
  border-radius: 4px;

  transition-property: border-color, background-color;
  transition-duration: 100ms;
  transition-timing-function: ease-in-out;

  &:checked {
    background-color: ${BRAND_COLORS.darkGreen};
    border-color: ${BRAND_COLORS.darkGreen};

    &::before {
      content: url(${IconCheckDataUri});
      display: block;
      width: 16px;
      height: 16px;
      color: ${BRAND_COLORS.white};
    }
  }

  ${applyStylesForScreenSize(
    [SCREEN_SIZES.PHONE],
    css`
      width: 24px;
      height: 24px;

      &:checked::before {
        content: url(${IconCheckDataUriMobile});
        width: 20px;
        height: 20px;
      }
    `,
  )}
`;

export const FormTextarea = styled(FormInput).attrs({ as: 'textarea' })`
  height: 80px;
  resize: none;
`;

export const ErrorMessage = styled(BaseBodyText)`
  color: ${BRAND_COLORS.darkRed};
  font-size: 12px;
  line-height: 20px;
`;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const FormError = styled(Alert).attrs({
  theme: ALERT_THEMES.error,
  customIcon: <IconInfoCircle />,
})``;

export const FormAutocomplete = styled(Autocomplete).attrs({
  isRelative: true,
})<FormInputProps>`
  .autocomplete-search-input {
    ${commonInputStyles}

    .ant-input {
      ${TYPOGRAPHY.CIRCULAR.book}
    }

    box-shadow: none;

    &:not(:disabled):hover {
      box-shadow: none;
      border-color: ${BRAND_COLORS.stroke};
    }

    ${({ $isInvalid }) => $isInvalid && css`
      /* Other styles are overriding the border on hover, so we need !important */
      border-color: ${BRAND_COLORS.darkRed} !important;
    `}

    &:has(.ant-input:focus-visible) {
      border-color: ${BRAND_COLORS.lightGreen};
    }
  }
`;

export const FormToggle = styled(ToggleSelect)``;